import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Image, Upload, Save, Eye, EyeOff, Youtube, X, Loader2 } from 'lucide-react';
import { YouTubePlayer } from '@/components/youtube/YouTubePlayer';

interface HeroBackgroundSettings {
  id: number;
  background_image_url: string | null;
  overlay_opacity: number;
  is_active: boolean;
  youtube_video_url: string | null;
}

const HeroBackgroundSettings: React.FC = () => {
  const [settings, setSettings] = useState<HeroBackgroundSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewMode, setPreviewMode] = useState(false);
  const { toast } = useToast();

  // Estados para edição
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [youtubeEmbedCode, setYoutubeEmbedCode] = useState('<iframe width="560" height="315" src="https://www.youtube.com/embed/_YbogE3RGAA" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>');
  const [overlayOpacity, setOverlayOpacity] = useState([70]);

  useEffect(() => {
    fetchSettings();
    checkAvailableBuckets();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('hero_background_settings')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error) throw error;

      setSettings(data);
      setBackgroundImageUrl(data.background_image_url || '');
      setYoutubeEmbedCode(data.youtube_video_url || '<iframe width="560" height="315" src="https://www.youtube.com/embed/_YbogE3RGAA" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>');
      setOverlayOpacity([Math.round(data.overlay_opacity * 100)]);
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as configurações",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const checkAvailableBuckets = async () => {
    try {
      const { data: buckets, error } = await supabase.storage.listBuckets();
      if (!error && buckets) {
        console.log('📋 Buckets disponíveis no Storage:', buckets.map(b => ({
          name: b.name,
          public: b.public,
          created_at: b.created_at
        })));
      } else {
        console.error('❌ Erro ao listar buckets:', error);
      }
    } catch (error) {
      console.error('❌ Erro ao verificar buckets:', error);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('hero_background_settings')
        .update({
          background_image_url: backgroundImageUrl || null,
          youtube_video_url: youtubeEmbedCode || null,
          overlay_opacity: overlayOpacity[0] / 100,
          updated_at: new Date().toISOString()
        })
        .eq('id', settings.id);

      if (error) throw error;

      await fetchSettings();
      toast({
        title: "Sucesso!",
        description: "Configurações do hero atualizadas com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar as configurações",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Erro",
        description: "Por favor, selecione um arquivo de imagem",
        variant: "destructive"
      });
      return;
    }

    // Validar tamanho (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Erro",
        description: "A imagem deve ter no máximo 10MB",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Criar nome único para o arquivo
      const fileExt = file.name.split('.').pop();
      const fileName = `hero-background-${Date.now()}.${fileExt}`;
      const filePath = `hero-backgrounds/${fileName}`;

      // Lista de buckets para tentar em ordem de preferência
      const bucketOptions = ['images', 'games', 'profile-images', 'banners', 'uploads'];
      let successfulBucket = null;
      let finalUrl = null;

      // Simular progresso de upload
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Tentar upload em cada bucket até encontrar um que funcione
      for (const bucketName of bucketOptions) {
        try {
          console.log(`🔄 Tentando upload no bucket: ${bucketName}`);
          
          // Remover arquivo anterior se existir no bucket atual
          if (backgroundImageUrl && backgroundImageUrl.includes(`/${bucketName}/`)) {
            const oldFileName = backgroundImageUrl.split('/').pop();
            if (oldFileName) {
              await supabase.storage
                .from(bucketName)
                .remove([oldFileName]);
            }
          }

          // Fazer upload do arquivo
          const { data, error } = await supabase.storage
            .from(bucketName)
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: true
            });

          if (!error && data) {
            // Obter URL pública
            const { data: urlData } = supabase.storage
              .from(bucketName)
              .getPublicUrl(filePath);

            if (urlData?.publicUrl) {
              successfulBucket = bucketName;
              finalUrl = urlData.publicUrl;
              console.log(`✅ Upload bem-sucedido no bucket: ${bucketName}`);
              break;
            }
          } else {
            console.log(`❌ Falha no upload no bucket ${bucketName}:`, error?.message);
          }
        } catch (bucketError) {
          console.log(`❌ Erro no bucket ${bucketName}:`, bucketError);
        }
      }

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (successfulBucket && finalUrl) {
        setBackgroundImageUrl(finalUrl);
        toast({
          title: "Sucesso!",
          description: `Imagem enviada com sucesso! (Bucket: ${successfulBucket})`,
        });
      } else {
        throw new Error('Não foi possível fazer upload em nenhum bucket disponível');
      }

    } catch (error) {
      console.error('Erro no upload:', error);
      toast({
        title: "Erro no upload",
        description: "Não foi possível enviar a imagem. Tente novamente ou use uma URL externa.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Limpar o input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleRemoveImage = async () => {
    if (!backgroundImageUrl) return;

    // Detectar qual bucket está sendo usado pela URL
    const bucketOptions = ['images', 'games', 'profile-images', 'banners', 'uploads'];
    let detectedBucket = null;

    for (const bucketName of bucketOptions) {
      if (backgroundImageUrl.includes(`/${bucketName}/`)) {
        detectedBucket = bucketName;
        break;
      }
    }

    // Se a imagem for do nosso storage, remover do Supabase
    if (detectedBucket) {
      try {
        const fileName = backgroundImageUrl.split('/').pop();
        if (fileName) {
          console.log(`🗑️ Removendo arquivo do bucket: ${detectedBucket}`);
          await supabase.storage
            .from(detectedBucket)
            .remove([fileName]);
          
          console.log(`✅ Arquivo removido com sucesso do bucket: ${detectedBucket}`);
        }
      } catch (error) {
        console.error('Erro ao remover arquivo:', error);
      }
    }

    setBackgroundImageUrl('');
    toast({
      title: "Imagem removida",
      description: "A imagem de fundo foi removida com sucesso",
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00B6F0]"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Image className="h-6 w-6 text-[#00B6F0]" />
            <div>
              <CardTitle className="text-white">Configurações do Hero</CardTitle>
              <CardDescription>
                Gerencie a imagem de fundo, vídeo e sobreposição da seção principal do site
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Upload e URL da Imagem */}
          <div className="space-y-4">
            <Label className="text-white">
              Imagem de Fundo
            </Label>
            
            {/* Upload de arquivo */}
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <Button 
                    variant="outline" 
                    className="w-full border-[#2D3748] border-dashed border-2 h-12 text-gray-400 hover:text-white hover:border-[#00B6F0]"
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Enviando... {uploadProgress}%
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Fazer Upload de Imagem
                      </>
                    )}
                  </Button>
                </div>
                
                {backgroundImageUrl && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleRemoveImage}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500"
                    disabled={uploading}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Barra de progresso do upload */}
              {uploading && (
                <div className="w-full bg-[#1A2233] rounded-full h-2">
                  <div 
                    className="bg-[#00B6F0] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}

              <p className="text-sm text-gray-400">
                Máximo 10MB • Formatos: JPG, PNG, WebP, GIF
              </p>
            </div>

            {/* Divisor */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-[#2D3748]"></div>
              <span className="text-gray-400 text-sm">ou</span>
              <div className="flex-1 h-px bg-[#2D3748]"></div>
            </div>

            {/* URL externa */}
            <div className="space-y-2">
              <Label htmlFor="backgroundImage" className="text-white">
                URL Externa da Imagem
              </Label>
              <Input
                id="backgroundImage"
                type="url"
                placeholder="https://exemplo.com/imagem.jpg"
                value={backgroundImageUrl}
                onChange={(e) => setBackgroundImageUrl(e.target.value)}
                className="bg-[#1A2233] border-[#2D3748] text-white"
                disabled={uploading}
              />
              <p className="text-sm text-gray-400">
                Cole a URL de uma imagem externa
              </p>
            </div>
          </div>

          {/* Código Embed do YouTube */}
          <div className="space-y-2">
            <Label htmlFor="youtubeEmbed" className="text-white flex items-center gap-2">
              <Youtube className="h-4 w-4 text-red-500" />
              Código Embed do YouTube
            </Label>
            <Textarea
              id="youtubeEmbed"
              placeholder='<iframe width="560" height="315" src="https://www.youtube.com/embed/..." title="YouTube video player" frameborder="0" allow="..." allowfullscreen></iframe>'
              value={youtubeEmbedCode}
              onChange={(e) => setYoutubeEmbedCode(e.target.value)}
              className="bg-[#1A2233] border-[#2D3748] text-white min-h-[100px] resize-none"
            />
            <p className="text-sm text-gray-400">
              Cole o código embed completo do YouTube (permite controle total dos parâmetros como autoplay, start time, etc.)
            </p>
          </div>

          {/* Opacidade da Sobreposição */}
          <div className="space-y-3">
            <Label className="text-white">
              Opacidade da Sobreposição: {overlayOpacity[0]}%
            </Label>
            <Slider
              value={overlayOpacity}
              onValueChange={setOverlayOpacity}
              max={100}
              min={0}
              step={5}
              className="w-full"
            />
            <p className="text-sm text-gray-400">
              Controla a transparência da sobreposição escura sobre a imagem
            </p>
          </div>

          {/* Preview da Imagem */}
          {backgroundImageUrl && (
            <div className="space-y-3">
              <Label className="text-white">Preview da Imagem</Label>
              <div className="relative h-32 rounded-lg overflow-hidden border border-[#2D3748]">
                <img
                  src={backgroundImageUrl}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://placehold.co/600x200/1A2233/00B6F0?text=Erro+ao+carregar';
                  }}
                />
                <div 
                  className="absolute inset-0 bg-black"
                  style={{ opacity: overlayOpacity[0] / 100 }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-white font-bold text-lg">
                    Sua Jornada Pró começa aqui!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Preview do Vídeo YouTube */}
          {youtubeEmbedCode && (
            <div className="space-y-3">
              <Label className="text-white flex items-center gap-2">
                <Youtube className="h-4 w-4 text-red-500" />
                Preview do Vídeo YouTube
              </Label>
              <div className="max-w-md">
                <YouTubePlayer embedCode={youtubeEmbedCode} />
              </div>
            </div>
          )}

          {/* Botões de Ação */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#00B6F0] text-black hover:bg-[#0088CC]"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Salvar Configurações
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setPreviewMode(!previewMode)}
              className="border-[#2D3748]"
            >
              {previewMode ? (
                <EyeOff className="h-4 w-4 mr-2" />
              ) : (
                <Eye className="h-4 w-4 mr-2" />
              )}
              {previewMode ? 'Sair do Preview' : 'Preview na Home'}
            </Button>

            {/* Botão de Debug - Teste de Upload */}
            <Button
              variant="outline"
              onClick={checkAvailableBuckets}
              className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
            >
              🔍 Verificar Buckets
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dicas */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="bg-[#0B1426] border-[#1E3A5F]">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#00B6F0]/20 rounded-lg mt-0.5">
                <Image className="h-4 w-4 text-[#00B6F0]" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">💡 Dicas para imagem</h4>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li>• Upload automático: máximo 10MB</li>
                  <li>• Use resolução mínima de 1920x1080px</li>
                  <li>• Prefira imagens relacionadas a games</li>
                  <li>• A sobreposição garante legibilidade</li>
                  <li>• Imagens enviadas ficam armazenadas no nosso servidor</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0B1426] border-[#1E3A5F]">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg mt-0.5">
                <Youtube className="h-4 w-4 text-red-500" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">🎥 Dicas para vídeo</h4>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li>• Use código embed completo do YouTube</li>
                  <li>• Controle autoplay, start time, loop, etc.</li>
                  <li>• Vídeos substituem a imagem quando configurados</li>
                  <li>• Preview em tempo real disponível</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HeroBackgroundSettings; 